import React from 'react';
import { Link } from 'react-router-dom';

// Common components
import HtmlContent from '../components/common/HtmlContent';
import ToolsTable from '../components/tools/ToolsTable';
import useHashScroll from '../hooks/useHashScroll';

/**
 * ResearchPage Component
 * 
 * Displays all research content just like the legacy HTML site
 * where multiple HTML files are combined (intro.html, problem.html, 
 * empathise.html, mapping.html, synthesis.html).
 */
const ResearchPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();

  // Research intro content - matching the original intro.html structure
  const researchIntroHtml = `
    <p>
    Research is the key - it might sound obvious, but something we all do is jump straight to solutions. Coming up with ideas to a pre-perceived problem that is littered with assumptions. This sometimes works, but usually leads to failure or a poorly crafted solution.
    </p>
    <p>
    Part of this desire to skip the research phase is sometimes borne from the lack of faith in the process, the ability to feel truly confident that all the work required will help with the eventual solution and testing assumptions. This is the most resource intensive step, but also the most important - don't skip it. 
    </p>
  `;

  // Problem section content
  const problemHtml = `
    <p>
      The problem comes to you - it could be something you've noticed, something you've experienced, something you've heard about and can often manifest itself as an idea of a solution to this imagined problem.
    </p>
    <p>
      However, taking the time to fully understand the problem itself first will make the likelihood of finding the right solution much higher.
    </p>
    <p>
      This section covers the discovery of that problem, and the various tools that can be used to frame, prioritise and define it further. 
    </p>
    <h4>Problem Tools</h4>
  `;

  // Empathise section content
  const empathiseHtml = `
    <p>
      We've discovered problems, we've prioritised and framed them and now we need to validate that such a problem exists and test our assumptions. 
    </p>
    <p>
      We do this through investigative research, speaking to users and stakeholders, looking at data and observing competitors.         
    </p>
    <p>
      This section contains the tools to help guide and structure the validation process. 
    </p>
    <h4>Empathise Tools</h4>
  `;

  // Mapping section content
  const mappingHtml = `
    <p>
      Once we've thoroughly interrogated the problem space and collated lots of research and now we need to understand it. We can do this by grouping similar themes, extracting insights, fully understanding our users and their needs and wants.
    </p>
    <p>
      By visually mapping and linking insights, it helps to build a bigger picture of the problem space and like panning for gold, combing through all this data with a fine tooth comb will extract value constantly, and mapping this information will help immensely with the decision making process.    
    </p>
    <h4>Mapping Tools</h4>
  `;

  // Synthesis section content
  const synthesisHtml = `
    <p>
      Taking the mapped insights we can begin to understand and define the problem in a way that moves us towards the ideation and solution phase. 
    </p>
    <p>
      Encouraging ourselves to interpret things in different ways is also important, and the tools in this phase allows us the opportunity for real innovation.
    </p>
    <h4>Synthesis Tools</h4>
  `;

  return (
    <>
      {/* Research Intro - matching original intro.html structure */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon"><i className="fas fa-bullhorn fa-2x"></i></div>
            <div className="tool-breadcrumb-nav">
              <Link to="/">R&D Toolkit</Link>&nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
              <Link to="/research">Research Tools</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/research">Research</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={researchIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Tool Sections</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#problem">Problem</Link></div>
            <div><Link className="tool-section-cta" to="#empathise">Empathise</Link></div>
            <div><Link className="tool-section-cta" to="#mapping">Mapping</Link></div>
            <div><Link className="tool-section-cta" to="#synthesis">Synthesis</Link></div>
          </div>
        </div>
      </div>

      {/* Problem Section */}
      <main className="case-study">
        <span className="case-tool-title" id="problem">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="problem">
            <span className="tool-title-number">
              <Link to={`/problem`}>Problem</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/">R&D Toolkit</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to={`/research`}>Research</Link>
          </div>
          <div className="tool-title-link">
            <Link to={`/problem`}><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div id="tool-box-styling-grandparent" className="tool-box-styling">
          <div className="tool-opening-container">
            <div className="tool-opening">
              <HtmlContent html={problemHtml} containerClass="" />
            </div>
          </div>
        </div>
        <div id="tabs-grandparent" className="tabs">
          {/* Problem - Discovery tab */}
          <input 
            type="radio" 
            name="tabs-problem" 
            id="tab-discovery" 
            defaultChecked
          />
          <label htmlFor="tab-discovery">Discovery</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/problem/discovery/#problem-log'} className="link-table">Problem Log</Link></td>
                  <td>Keep track of problems to be further investigated.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/discovery/problem-log.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpGUsJk=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/discovery/#workshop'} className="link-table">Problem Workshop</Link></td>
                  <td>Bring a team together to discuss, analyse and prioritise problems to be solved.</td>
                  <td>Multiple</td>
                </tr>
                <tr>
                  <td><Link to={'/problem/discovery/#problem-interview'} className="link-table">Problem Interview</Link></td>
                  <td>Discover the problem from the source through an interview.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/discovery/problem-interview-sheet.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpWNnvM=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/discovery/#5-whys'} className="link-table">5 Whys</Link></td>
                  <td>Go deeper with understanding the problem.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/discovery/5-whys.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpXATWs=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/discovery/#sailboat'} className="link-table">Sailboat</Link></td>
                  <td>Discover future problems that may occur.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/discovery/sailboat.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpKGzwI=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Problem - Prioritisation tab */}
          <input 
            type="radio" 
            name="tabs-problem" 
            id="tab-prioritisation"
          />
          <label htmlFor="tab-prioritisation">Prioritisation</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/problem/prioritisation/#priority-matrix'} className="link-table">Priority Matrix</Link></td>
                  <td>Visually plot problems in order to prioritise which should be worked on.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/prioritisation/priority-matrix.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpPWRDY=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/prioritisation/#swot-analysis'} className="link-table">SWOT Analysis</Link></td>
                  <td>Look inwards to help focus on the problems that are aligned with the business goals.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/prioritisation/swot-analysis.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpd-ZfQ=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/prioritisation/#nuf-test'} className="link-table">NUF Test</Link></td>
                  <td>Add some quantitative data to the problem to help prioritise the best problem to work on.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/prioritisation/nuf-test.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpTbPcI=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/prioritisation/#priority-chart'} className="link-table">Priority Chart</Link></td>
                  <td>Weigh up effort vs reward as a means of prioritising problems.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/prioritisation/priority-chart.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpdLcxs=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Problem - Project Scoping tab */}
          <input 
            type="radio" 
            name="tabs-problem" 
            id="tab-project-scoping"
          />
          <label htmlFor="tab-project-scoping">Project Scoping</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/problem/project-scoping/#project-canvas'} className="link-table">Project Canvas</Link></td>
                  <td>Outline the scope of the project, including tools and resources.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/project-scoping/project-canvas.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kogauXM=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/project-scoping/#lean-canvas'} className="link-table">Lean Canvas</Link></td>
                  <td>Have more focus on speed and efficiency when outlining the project.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/project-scoping/lean-canvas.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_knFghvg=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/project-scoping/#project-brief'} className="link-table">Project Brief</Link></td>
                  <td>Clarify what the goals and processes that are expected from the project are.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/project-scoping/project-brief.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_knh7e64=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/project-scoping/#deliverables'} className="link-table">Deliverables</Link></td>
                  <td>Document the exact things that are expected to be delivered during and at the end of the project.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/project-scoping/deliverables.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_knu7jLs=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Problem - Framing tab */}
          <input 
            type="radio" 
            name="tabs-problem" 
            id="tab-framing"
          />
          <label htmlFor="tab-framing">Framing</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/problem/framing/#problem-statement'} className="link-table">Problem Statement</Link></td>
                  <td>Use to state the problem in a consistent and clear way.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/framing/problem-statement.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpeg0X8=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/framing/#hypothesis'} className="link-table">Hypothesis</Link></td>
                  <td>Align why solving the problem will help the user achieve their goal and the business their goal.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/framing/hypothesis.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpe3byQ=/?" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/framing/#problem-framing-worksheet'} className="link-table">Problem Framing Worksheet</Link></td>
                  <td>A method for framing the problem, what activities should be conducted and the goals, reducing crossover.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/framing/problem-framing-worksheet.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpeUcnM=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/framing/#problem-framing-sketch'} className="link-table">Problem Framing Sketch</Link></td>
                  <td>Visualise the problem as a means of removing assumptions that could affect the perception of the problem.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/framing/problem-framing-sketch.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpfLglY=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/problem/framing/#proto-persona'} className="link-table">Proto-Persona</Link></td>
                  <td>Build empathy by creating a Persona of someone who could be experiencing the problem.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/problem/framing/proto-persona.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpd1bW8=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Empathise Section */}
      <main className="case-study">
        <span className="case-tool-title" id="empathise">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="empathise">
            <span className="tool-title-number">
              <Link to={`/empathise`}>Empathise</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/">R&D Toolkit</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to={`/research`}>Research</Link>
          </div>
          <div className="tool-title-link">
            <Link to={`/empathise`}><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div id="tool-box-styling-grandparent" className="tool-box-styling">
          <div className="tool-opening-container">
            <div className="tool-opening">
              <HtmlContent html={empathiseHtml} containerClass="" />
            </div>
          </div>
        </div>
        <div id="tabs-grandparent" className="tabs">
          {/* Empathise - Investigate tab */}
          <input 
            type="radio" 
            name="tabs-empathise" 
            id="tab-empathise-investigate" 
            defaultChecked
          />
          <label htmlFor="tab-empathise-investigate">Investigate</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/investigate/#research-canvas'} className="link-table">Research Canvas</Link></td>
                  <td>A single source of truth for how the Research phase will proceed.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/investigate/research-canvas.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kois3So=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/investigate/#competitor-analysis'} className="link-table">Competitor Analysis</Link></td>
                  <td>Has anyone tackled this problem before? How? What products are users using to help solve the problem?</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/investigate/competitor-analysis.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koh1sjE=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/investigate/#stakeholder-interview'} className="link-table">Stakeholder Interview</Link></td>
                  <td>Further understand the problem from those closest to the coal face.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/investigate/stakeholder-interview.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_konMyto=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Empathise - User Interview tab */}
          <input 
            type="radio" 
            name="tabs-empathise" 
            id="tab-empathise-user-interview"
          />
          <label htmlFor="tab-empathise-user-interview">User Interview</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/user-interview/#recruiting'} className="link-table">Recruiting</Link></td>
                  <td>How to target and recruit the right users to interview.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/user-interview/recruiting-users.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kovZjAA=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/user-interview/#screener-survey'} className="link-table">Screener Survey</Link></td>
                  <td>A vital and lean method of finding target users and gathering general feedback data.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/user-interview/screener-survey.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koq5zRg=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/user-interview/#interview-structure'} className="link-table">Interview Structure</Link></td>
                  <td>How to efficiently and thoroughly conduct an interview to maximise insight.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/user-interview/tab-interview-structure-template.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kouSxJY=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/user-interview/#question-structure'} className="link-table">Question Structure</Link></td>
                  <td>Structure questions in a way that will help conversation flow and insights appear.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/user-interview/question-structure.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kotLxwU=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/user-interview/#the-interview'} className="link-table">The Interview</Link></td>
                  <td>Conduct the interview and capture the output in a concise, digestible way.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/user-interview/the-interview.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koyDEPs=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          {/* Empathise - User Insights tab */}
          <input 
            type="radio" 
            name="tabs-empathise" 
            id="tab-empathise-user-insights"
          />
          <label htmlFor="tab-empathise-user-insights">User Insights</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/user-insights/#competitor-usability'} className="link-table">Competitor Usability</Link></td>
                  <td>Observe and discover how uses solve their problem using competitor's products.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/user-insights/competitor-usability.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_klAHEUU=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/user-insights/#contextual-inquiry'} className="link-table">Contextual Inquiry</Link></td>
                  <td>Observe a user experience the problem in their context to understand what they go through.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/user-insights/contextual-inquiry.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_klNlFE4=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/user-insights/#card-sorting'} className="link-table">Card Sorting</Link></td>
                  <td>Understand the best way to structure a product, and how users think whilst navigating.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/user-insights/card-sorting.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_klMv5cI=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Empathise - Data tab */}
          <input 
            type="radio" 
            name="tabs-empathise" 
            id="tab-empathise-data"
          />
          <label htmlFor="tab-empathise-data">Data</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/data/#analytics'} className="link-table">Analytics</Link></td>
                  <td>Use existing products and usage data to uncover problems and opportunities.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/data/analytics.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_ko0a1O4=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/data/#ab-testing'} className="link-table">A/B Testing</Link></td>
                  <td>Understand user's preferences and motivations by testing different options.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/data/ab-testing.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_ko_YL0g=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/empathise/data/#questionnaire'} className="link-table">Questionnaire</Link></td>
                  <td>Gather large amounts of high level data to further understand trends and problems.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/empathise/data/questionnaire.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koC7OMg=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Mapping Section */}
      <main className="case-study">
        <span className="case-tool-title" id="mapping">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="mapping">
            <span className="tool-title-number">
              <Link to={`/mapping`}>Mapping</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/">R&D Toolkit</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to={`/research`}>Research</Link>
          </div>
          <div className="tool-title-link">
            <Link to={`/mapping`}><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div id="tool-box-styling-grandparent" className="tool-box-styling">
          <div className="tool-opening-container">
            <div className="tool-opening">
              <HtmlContent html={mappingHtml} containerClass="" />
            </div>
          </div>
        </div>
        <div id="tabs-grandparent" className="tabs">
          {/* Mapping - Research Mapping tab */}
          <input 
            type="radio" 
            name="tabs-mapping" 
            id="tab-mapping-research-mapping" 
            defaultChecked
          />
          <label htmlFor="tab-mapping-research-mapping">Research Mapping</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/mapping/research-mapping/#summarising-research'} className="link-table">Summarising Research</Link></td>
                  <td>Efficiently collect, summarise and present research insights.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/mapping/research-mapping/summarising-research.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koGnCx8=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/mapping/research-mapping/#affinity-map'} className="link-table">Affinity Map</Link></td>
                  <td>Spot trends and patterns by visual grouping of research insights and data.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/mapping/research-mapping/affinity-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koG53mo=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/mapping/research-mapping/#concept-mapping'} className="link-table">Concept Mapping</Link></td>
                  <td>Combine existing and new knowledge to connect insights in a way that can lead to an idea or product feature.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/mapping/research-mapping/concept-mapping.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koFdq4Y=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/mapping/research-mapping/#assumptions-map'} className="link-table">Assumptions Map</Link></td>
                  <td>Map and test key assumptions by deconstructing and mapping them to check they are still valid.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/mapping/research-mapping/assumpions-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koIzr6o=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mapping - User Mapping tab */}
          <input 
            type="radio" 
            name="tabs-mapping" 
            id="tab-mapping-user-mapping"
          />
          <label htmlFor="tab-mapping-user-mapping">User Mapping</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/mapping/user-mapping/#persona'} className="link-table">Persona</Link></td>
                  <td>Build empathy and understanding by creating a typical user type.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/mapping/user-mapping/persona.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koNA_gk=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/mapping/user-mapping/#empathy-map'} className="link-table">Empathy Map</Link></td>
                  <td>Understand user's and their problems further by understanding what they are trying to accomplish and their experience doing it.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/mapping/user-mapping/empathy-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koM0lHM=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/mapping/user-mapping/#customer-journey-map'} className="link-table">Customer Journey Map</Link></td>
                  <td>A visual way of showing what a user experiences whilst trying to reach their goal.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/mapping/user-mapping/customer-journey-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koMJTZw=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/mapping/user-mapping/#experience-map'} className="link-table">Experience Map</Link></td>
                  <td>Understand what a user goes through when trying to achieve their goal outside of a product.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/mapping/user-mapping/experience-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koM9mDE=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mapping - Organisation Mapping tab */}
          <input 
            type="radio" 
            name="tabs-mapping" 
            id="tab-mapping-organisation-mapping"
          />
          <label htmlFor="tab-mapping-organisation-mapping">Organisation Mapping</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/mapping/organisation-mapping/#stakeholder-map'} className="link-table">Stakeholder Map</Link></td>
                  <td>Connect the various stakeholders in an organisation that could effect and influence the product development process.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/mapping/organisation-mapping/stakeholder-map.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koJ8hoY=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/mapping/organisation-mapping/#service-blueprint'} className="link-table">Service Blueprint</Link></td>
                  <td>There are many other hidden functions that go in to a functional product and a Service Blueprint will help map these.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/mapping/organisation-mapping/service-blueprint.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koRsj9s=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Synthesis Section */}
      <main className="case-study">
        <span className="case-tool-title" id="synthesis">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="synthesis">
            <span className="tool-title-number">
              <Link to={`/synthesis`}>Synthesis</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/">R&D Toolkit</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to={`/research`}>Research</Link>
          </div>
          <div className="tool-title-link">
            <Link to={`/synthesis`}><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div id="tool-box-styling-grandparent" className="tool-box-styling">
          <div className="tool-opening-container">
            <div className="tool-opening">
              <HtmlContent html={synthesisHtml} containerClass="" />
            </div>
          </div>
        </div>
        <div id="tabs-grandparent" className="tabs">
          {/* Synthesis - Problem Reframing tab */}
          <input 
            type="radio" 
            name="tabs-synthesis" 
            id="tab-synthesis-problem-reframing" 
            defaultChecked
          />
          <label htmlFor="tab-synthesis-problem-reframing">Problem Reframing</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/problem-reframing/#concept'} className="link-table">Concept</Link></td>
                  <td>Look at the problem from a different perspective, helping with more diverse and leaner solutions.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/problem-reframing/problem-reframing.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpPaIPU=/?" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/problem-reframing/#root-cause-analysis'} className="link-table">Root Cause Analysis</Link></td>
                  <td>Dive deeper on the problem to come up with alternate ways of looking at the cause.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/problem-reframing/root-cause-analysis.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koRWxOo=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/problem-reframing/#reframing-matrix'} className="link-table">Reframing Matrix</Link></td>
                  <td>Bring people together from different perspectives to focus on 4 key areas to look at the problem in different ways.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/problem-reframing/reframing-matrix.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koQ9oEc=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/problem-reframing/#challenge-mapping'} className="link-table">Challenge Mapping</Link></td>
                  <td>Use 'How Might We' in a way that helps to dive deeper in to the problem from 2 different directions - 'Why' and 'What's Stopping Us'</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/problem-reframing/challenge-mapping.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koX_sJo=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Synthesis - Define tab */}
          <input 
            type="radio" 
            name="tabs-synthesis" 
            id="tab-synthesis-define"
          />
          <label htmlFor="tab-synthesis-define">Define</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/define/#5-ws'} className="link-table">5 Ws</Link></td>
                  <td>Use the 5 Whys as a method of interrogating, and ultimately defining the true problem.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/define/5-ws.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_koWzAkU=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/define/#scenarios'} className="link-table">Scenarios</Link></td>
                  <td>Small stories that help define the problem and understand the user goal.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/define/scenarios.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kobIlwM=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/define/#job-story'} className="link-table">Job Story</Link></td>
                  <td>Define what the user is trying to accomplish in a succinct way that a development team can fully understand.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/define/job-story.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kobcwks=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/define/#user-story'} className="link-table">User Story</Link></td>
                  <td>Tie a user persona to a specific task that a user would like to accomplish in a consistent format that the development team can follow.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/define/user-story.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kmSDS0Q=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/define/#how-might-we'} className="link-table">How Might We</Link></td>
                  <td>Define a user need in an optimistic way that moves away from a problem and towards a solution.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/define/how-might-we.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_km2_d70=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Synthesis - Feature Prioritisation tab */}
          <input 
            type="radio" 
            name="tabs-synthesis" 
            id="tab-synthesis-feature-prioritisation"
          />
          <label htmlFor="tab-synthesis-feature-prioritisation">Feature Prioritisation</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/feature-prioritisation/#feature-log'} className="link-table">Feature Log</Link></td>
                  <td>Keep track of features that could be used in current or future Product Development.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/feature-prioritisation/feature-log.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kiGWGjc=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/feature-prioritisation/#moscow'} className="link-table">MoSCoW</Link></td>
                  <td>A prioritisation method that helps focus minds on the most vital elements and can also help define an MVP.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/feature-prioritisation/moscow.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kiGd8KU=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/feature-prioritisation/#weighted-scoring'} className="link-table">Weighted Scoring</Link></td>
                  <td>Build empathy across stakeholders and an organisation by prioritising features based on their impact on both the user and business.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/feature-prioritisation/weighted-scoring.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kiFmuLE=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to={'/synthesis/feature-prioritisation/#buy-a-feature'} className="link-table">Buy-a-Feature</Link></td>
                  <td>Involve stakeholders in the feature prioritisation process by allowing them to choose features based on a specific budget.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/synthesis/feature-prioritisation/buy-a-feature.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kiQ5w2o=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </>
  );
};

export default ResearchPage;